import { useState, useEffect, useRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal, MerchantButton, SecondaryButton, FormLabel, DetailsTextarea, ReasonPopover, ReasonGrid } from "@components";
import { createDispute } from "@api";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ExclamationCircleIcon, PlusIcon } from "@heroicons/react/24/outline";

const DISPUTE_REASONS = [
  { value: "ThisIsntMyInvoice", label: "This isn't my invoice" },
  { value: "NeedCopyOfInvoice", label: "Need a copy of this invoice" },
  { value: "NeedCopyOfInvoiceOrStatement", label: "Need a copy of an invoice or statement" },
  { value: "PriceDoesntMatch", label: "Price doesn't match" },
  { value: "OrderNotDelivered", label: "Order not delivered" },
  { value: "MissingItemsShortDelivery", label: "Missing items / short delivery" },
  { value: "ReturnNotCredited", label: "Return not credited" },
  { value: "DamagedOnDelivery", label: "Damaged on delivery" },
  { value: "WrongItemSupplied", label: "Wrong item supplied" },
  { value: "BilledTwice", label: "Billed twice" },
  { value: "PaymentAllocatedWrong", label: "Payment allocated wrong" },
  { value: "CreditLimitQuery", label: "Credit limit query" },
  { value: "SomethingElse", label: "Something else" },
];

const DETAILS_MAX = 500;

export const DisputeModal = ({
  isOpen,
  onClose,
  onSuccess,
  ledgerTransactionId,
  lineItems,
  invoiceNumber,
  merchant,
  onRefreshStatement,
}) => {
  const isLineItemDispute = lineItems?.length > 0;
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, touchedFields, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: isLineItemDispute
      ? { items: lineItems.map(li => ({ lineItemId: li.id, reason: "", details: "" })) }
      : { reason: "", details: "" },
  });

  const { fields } = useFieldArray({ control, name: "items" });

  useEffect(() => {
    if (!isOpen) return;
    setExpandedNotes(new Set());
    reset(
      isLineItemDispute
        ? { items: lineItems.map(li => ({ lineItemId: li.id, reason: "", details: "" })) }
        : { reason: "", details: "" }
    );
  }, [isOpen]);

  const { mutate, isPending, error: mutationError, reset: resetMutation } = useMutation({
    mutationFn: (data) => {
      if (isLineItemDispute) {
        return createDispute({
          ledgerTransactionId,
          lineItems: data.items.map(item => ({
            lineItemId: item.lineItemId,
            reason: item.reason,
            details: item.details || undefined,
          })),
          reason: undefined,
          details: undefined,
        });
      }
      return createDispute({
        ledgerTransactionId,
        lineItems: undefined,
        reason: data.reason,
        details: data.details || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceDetails"] });
      onRefreshStatement?.();
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setExpandedNotes(new Set());
        reset();
        resetMutation();
        onSuccess();
      }, 1500);
    },
  });

  const handleClose = () => {
    if (isPending) return;
    setExpandedNotes(new Set());
    reset();
    resetMutation();
    setShowSuccess(false);
    onClose();
  };

  const modalTitle = !isLineItemDispute
    ? `Dispute ${invoiceNumber}`
    : lineItems.length === 1
      ? `Dispute line item on ${invoiceNumber}`
      : `Dispute ${lineItems.length} line items on ${invoiceNumber}`;

  const errorMessage =
    mutationError?.response?.data?.message ||
    mutationError?.response?.data?.title ||
    mutationError?.message ||
    (mutationError ? "Something went wrong. Please try again." : null);

  const toggleNote = (index) =>
    setExpandedNotes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle}>
      <div className="p-5 sm:p-6 bg-white overflow-y-auto max-h-[80vh]">
        {showSuccess ? (
          <div className="flex flex-col items-center justify-center gap-4 py-10">
            <div className="relative flex items-center justify-center">
              <div className="size-20 rounded-full bg-primary-50" />
              <CheckCircleIcon className="size-10 text-primary-500 absolute" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-base font-semibold text-zinc-900">Dispute submitted</p>
              <p className="text-sm text-zinc-500">We&apos;ve received your dispute and will be in touch.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(data => mutate(data))} noValidate>
            <div className="flex flex-col gap-5">

              {isLineItemDispute ? (
                <div className="flex flex-col divide-y divide-zinc-100 -my-1">
                  {fields.map((field, index) => {
                    const lineItem = lineItems[index];
                    const itemErrors = errors.items?.[index];
                    const noteExpanded = expandedNotes.has(index);

                    return (
                      <div key={field.id} className="py-3.5 first:pt-0 last:pb-0 flex flex-col gap-2">
                        <p className="text-sm font-medium text-zinc-800 leading-snug">
                          {lineItem.description}
                        </p>

                        <div className="flex items-center gap-2 flex-wrap">
                          <Controller
                            name={`items.${index}.reason`}
                            control={control}
                            rules={{ required: "Please select a reason" }}
                            render={({ field: reasonField }) => (
                              <ReasonPopover
                                field={reasonField}
                                options={DISPUTE_REASONS}
                                isSubmitted={isSubmitted}
                              />
                            )}
                          />

                          <button
                            type="button"
                            onClick={() => toggleNote(index)}
                            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
                          >
                            {noteExpanded ? (
                              <span>Remove note</span>
                            ) : (
                              <>
                                <PlusIcon className="size-3" />
                                <span>Add note</span>
                              </>
                            )}
                          </button>
                        </div>

                        {itemErrors?.reason && isSubmitted && (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <ExclamationCircleIcon className="size-3.5 shrink-0" />
                            {itemErrors.reason.message}
                          </p>
                        )}

                        {noteExpanded && (
                          <Controller
                            name={`items.${index}.details`}
                            control={control}
                            render={({ field: detailsField }) => (
                              <DetailsTextarea
                                field={detailsField}
                                error={itemErrors?.details}
                                submitted={isSubmitted}
                                touched={!!touchedFields.items?.[index]?.details}
                                placeholder="Add a note about this item..."
                                compact
                                maxLength={DETAILS_MAX}
                              />
                            )}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <Controller
                    name="reason"
                    control={control}
                    rules={{ required: "Please select a reason", validate: v => v !== "" || "Please select a reason" }}
                    render={({ field }) => (
                      <ReasonGrid
                        field={field}
                        options={DISPUTE_REASONS}
                        error={errors.reason}
                        submitted={isSubmitted}
                        touched={!!touchedFields.reason}
                      />
                    )}
                  />
                  <div className="border-t border-zinc-100" />
                  <div>
                    <FormLabel htmlFor="dispute-details">
                      Details <span className="text-zinc-400 font-normal">(optional)</span>
                    </FormLabel>
                    <Controller
                      name="details"
                      control={control}
                      render={({ field }) => (
                        <DetailsTextarea
                          field={field}
                          error={errors.details}
                          submitted={isSubmitted}
                          touched={!!touchedFields.details}
                          placeholder="Describe the issue with this invoice..."
                          maxLength={DETAILS_MAX}
                        />
                      )}
                    />
                  </div>
                </>
              )}

              {errorMessage && !isPending && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                  <ExclamationCircleIcon className="size-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
                <SecondaryButton
                  onClick={handleClose}
                  disabled={isPending}
                  className="sm:flex-1 text-sm text-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </SecondaryButton>
                <MerchantButton
                  type="submit"
                  merchant={merchant}
                  loading={isPending}
                  disabled={isPending || !isValid}
                  className="sm:flex-1"
                >
                  Submit dispute
                </MerchantButton>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
